/**
 * The Asset Registry: loads content by reference, streams bytes with progress,
 * de-duplicates concurrent loads and caches results.
 *
 * Loaders are registered per asset kind. Assets owns the `json`, `seed` and
 * `level` loaders. The Renderer registers `gltf`/`ktx2` at its init (review
 * F-2), so this layer never imports render code.
 *
 * Asset types are declared through `AssetTypeMap`, which other layers extend
 * with declaration merging:
 *
 *   declare module "../assets/AssetRegistry" {
 *     interface AssetTypeMap { gltf: MeshAsset }
 *   }
 */
import { Emitter } from "../core/Emitter";
import type { Unsubscribe } from "../core/Emitter";
import type { Log } from "../core/Log";
import type { Fetcher } from "./Fetcher";

/** Maps asset kinds to the type their loader produces. Extended by other layers. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AssetTypeMap {}

/** The asset kinds with a declared type in AssetTypeMap. */
export type AssetKind = Extract<keyof AssetTypeMap, string>;

export interface AssetRef {
  readonly kind: string;
  /** Path relative to the content root, e.g. `levels/riot-games.level.json`. */
  readonly path: string;
  /** Expected decoded size from the content index, used when Content-Length is missing. */
  readonly bytes?: number;
  /** Content hash from the content index, appended as `?v=` for cache busting. */
  readonly hash?: string;
}

export type AssetLoader<T> = (
  bytes: Uint8Array,
  ref: AssetRef,
  registry: AssetRegistry,
) => T | Promise<T>;

export interface StreamProgress {
  readonly ref: AssetRef;
  readonly loaded: number;
  readonly total: number | null;
  readonly done: boolean;
}

export interface LoadOptions {
  readonly signal?: AbortSignal;
  /** The fetch's priority; see FetchOptions. A cached or in-flight load keeps its own. */
  readonly priority?: RequestPriority;
}

export interface AssetRegistryOptions {
  fetcher: Fetcher;
  /** Absolute URL prefix of the content folder, e.g. `/portfolio/content/`. */
  contentRoot: string;
  log: Log;
}

export class AssetRegistry {
  private readonly fetcher: Fetcher;
  private readonly contentRoot: string;
  private readonly log: Log;
  private readonly loaders = new Map<string, AssetLoader<unknown>>();
  private readonly cache = new Map<string, Promise<unknown>>();
  /** Bytes fetched but not yet decoded (prefetch), by URL; dropped once decoded. */
  private readonly bytes = new Map<string, Promise<Uint8Array>>();
  private readonly progress = new Emitter<{ progress: StreamProgress }>();

  constructor(options: AssetRegistryOptions) {
    this.fetcher = options.fetcher;
    this.contentRoot = options.contentRoot;
    this.log = options.log;
  }

  registerLoader<K extends AssetKind>(kind: K, loader: AssetLoader<AssetTypeMap[K]>): void;
  registerLoader(kind: string, loader: AssetLoader<unknown>): void;
  registerLoader(kind: string, loader: AssetLoader<unknown>): void {
    if (this.loaders.has(kind)) throw new Error(`Asset loader for '${kind}' is already registered`);
    this.loaders.set(kind, loader);
  }

  /** Absolute URL for a content-relative path (also used for plain links like Resume.pdf). */
  url(path: string, hash?: string): string {
    const base = `${this.contentRoot}${path.replace(/^\/+/, "")}`;
    return hash ? `${base}?v=${hash}` : base;
  }

  onProgress(listener: (event: StreamProgress) => void): Unsubscribe {
    return this.progress.on("progress", listener);
  }

  load<K extends AssetKind>(
    ref: AssetRef & { kind: K },
    options?: LoadOptions,
  ): Promise<AssetTypeMap[K]>;
  load(ref: AssetRef, options?: LoadOptions): Promise<unknown>;
  load(ref: AssetRef, options: LoadOptions = {}): Promise<unknown> {
    const url = this.url(ref.path, ref.hash);

    const cached = this.cache.get(url);
    if (cached) return cached;

    const pending = this.fetchAndDecode(ref, url, options);
    this.cache.set(url, pending);

    // Failed or aborted loads must be retryable, so they never stay cached.
    pending.catch(() => {
      if (this.cache.get(url) === pending) this.cache.delete(url);
    });

    return pending;
  }

  /**
   * Downloads an asset's bytes without decoding them, so a later `load` only
   * decodes: a level starts a large model's download with everything else, at
   * low priority, and decodes it once the level is shown. Resolves when the
   * bytes have arrived; a failed or aborted prefetch leaves nothing behind.
   */
  prefetch(ref: AssetRef, options: LoadOptions = {}): Promise<void> {
    const url = this.url(ref.path, ref.hash);
    if (this.cache.has(url)) return Promise.resolve();
    return this.fetchBytes(ref, url, options).then(() => undefined);
  }

  private async fetchAndDecode(ref: AssetRef, url: string, options: LoadOptions): Promise<unknown> {
    const loader = this.loaders.get(ref.kind);
    if (!loader) throw new Error(`No asset loader registered for '${ref.kind}'`);

    const bytes = await this.fetchBytes(ref, url, options);
    // The decoded asset is what's cached from here on.
    this.bytes.delete(url);
    return loader(bytes, ref, this);
  }

  /** The asset's bytes: a prefetch's, arrived or in flight, or a new fetch. */
  private fetchBytes(ref: AssetRef, url: string, options: LoadOptions): Promise<Uint8Array> {
    const inFlight = this.bytes.get(url);
    if (inFlight) return inFlight;

    const pending = this.download(ref, url, options);
    this.bytes.set(url, pending);
    pending.catch(() => {
      if (this.bytes.get(url) === pending) this.bytes.delete(url);
    });
    return pending;
  }

  private async download(ref: AssetRef, url: string, options: LoadOptions): Promise<Uint8Array> {
    const bytes = await this.fetcher.fetch(url, {
      ...(options.signal && { signal: options.signal }),
      ...(options.priority && { priority: options.priority }),
      onChunk: (loaded, total) => {
        this.progress.emit("progress", {
          ref,
          loaded,
          total: total ?? ref.bytes ?? null,
          done: false,
        });
      },
    });

    this.progress.emit("progress", {
      ref,
      loaded: bytes.byteLength,
      total: bytes.byteLength,
      done: true,
    });
    this.log.verbose("Stream", `${ref.path} · ${formatBytes(bytes.byteLength)}`);
    return bytes;
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
